/**
 * ConversationTimeline — Story 3.11.
 *
 * Renders a vertical timeline of conversation events: messages alternating
 * left (inbound) / right (outbound) and audit events as centered system
 * pills.
 *
 * Pure presentation: takes a `TimelineEntry[]` (already chronologically
 * sorted server-side) and renders it. The component does NOT fetch — that
 * is the page's job — so it stays trivially testable in isolation.
 *
 * Empty state shows "Aguardando primeira mensagem do cliente" per Story 3.11
 * §AC #5.
 */
import { Bell, MessageCircle, Send } from 'lucide-react'
import type { TimelineEntry } from '@sdr-agend/admin-schemas'

import { cn } from '@/lib/utils'

interface ConversationTimelineProps {
  entries: ReadonlyArray<TimelineEntry>
}

/**
 * Pretty-print an audit action for the timeline pill. Maps the dotted DB
 * key to a Portuguese label; falls back to the raw key when unknown so
 * future audit actions show up as soon as they are written without a code
 * deploy.
 */
const AUDIT_LABELS: Record<string, string> = {
  'conversation.intent_detected': 'Intent detectado',
  'conversation.message_in': 'Mensagem recebida',
  'conversation.message_out': 'Mensagem enviada',
  'conversation.cancellation_cascade': 'Cancelamento em cascata',
  'opt_out.detected': 'Opt-out detectado',
  'opt_out.applied': 'Opt-out aplicado',
  'handoff.triggered': 'Handoff acionado',
  'handoff.sla_breached': 'SLA de handoff vencido',
  'handoff.released_manual': 'Handoff liberado',
  'handoff.no_operators_configured': 'Sem operadores configurados',
  'compliance.window_breach_attempt': 'Tentativa fora da janela',
  'confirmation.c1_sent': 'C1 enviado',
  'confirmation.c2_sent': 'C2 enviado',
  'confirmation.c3_sent': 'C3 enviado',
  'confirmation.skipped_already_confirmed': 'C pulado (já confirmado)',
  'confirmation.cancelled': 'Confirmação cancelada',
  'confirmation.template_not_approved': 'Template não aprovado',
  // Story 3.20 — generic followup-by-inactivity engine.
  'followup.initial_sent': 'Contato inicial enviado',
  'followup.step_sent': 'Followup enviado',
  'conversation.cold': 'Conversa marcada como fria',
}

function formatAuditLabel(action: string): string {
  return AUDIT_LABELS[action] ?? action
}

/**
 * Format an ISO timestamp as `dd/MM HH:mm` (BR locale). Defensive: returns
 * the input unchanged when `Date.parse` fails so a malformed timestamp
 * cannot blow up the render tree.
 */
function formatOccurredAt(iso: string): string {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  const d = new Date(ms)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ConversationTimeline({
  entries,
}: ConversationTimelineProps): React.ReactElement {
  if (entries.length === 0) {
    return (
      <div
        role="status"
        className="flex flex-col items-center justify-center rounded-md border border-dashed py-12 text-sm text-muted-foreground"
      >
        <MessageCircle className="mb-2 h-6 w-6" aria-hidden="true" />
        <p>Aguardando primeira mensagem do cliente.</p>
      </div>
    )
  }

  return (
    <ol className="space-y-3" aria-label="Timeline da conversa">
      {entries.map((entry) =>
        entry.kind === 'message' ? (
          <MessageCard key={`m-${entry.id}`} entry={entry} />
        ) : (
          <AuditPill key={`a-${entry.id}`} entry={entry} />
        ),
      )}
    </ol>
  )
}

interface MessageCardProps {
  entry: Extract<TimelineEntry, { kind: 'message' }>
}

function MessageCard({ entry }: MessageCardProps): React.ReactElement {
  const isInbound = entry.direction === 'inbound'
  const Icon = isInbound ? MessageCircle : Send
  return (
    <li
      className={cn('flex w-full', isInbound ? 'justify-start' : 'justify-end')}
      data-testid={`timeline-message-${entry.direction}`}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-lg border px-3 py-2 text-sm shadow-sm',
          isInbound ? 'bg-muted/40' : 'bg-primary/10',
        )}
      >
        <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-3 w-3" aria-hidden="true" />
          <span>{isInbound ? 'Cliente' : 'Sistema'}</span>
          <span aria-hidden="true">·</span>
          <span>{formatOccurredAt(entry.occurredAt)}</span>
          {entry.type !== 'text' && (
            <>
              <span aria-hidden="true">·</span>
              <span className="uppercase">{entry.type}</span>
            </>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words">
          {entry.text === null || entry.text === '' ? (
            <span className="italic text-muted-foreground">(sem conteúdo)</span>
          ) : (
            entry.text
          )}
        </p>
        {(entry.intent !== null || entry.sentiment !== null) && (
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
            {entry.intent !== null && (
              <span className="rounded bg-background px-1.5 py-0.5">
                intent: <strong>{entry.intent}</strong>
              </span>
            )}
            {entry.sentiment !== null && (
              <span className="rounded bg-background px-1.5 py-0.5">
                sentiment: <strong>{entry.sentiment}</strong>
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

interface AuditPillProps {
  entry: Extract<TimelineEntry, { kind: 'audit' }>
}

function AuditPill({ entry }: AuditPillProps): React.ReactElement {
  return (
    <li className="flex w-full justify-center" data-testid="timeline-audit">
      <div className="flex max-w-[80%] items-center gap-2 rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground">
        <Bell className="h-3 w-3" aria-hidden="true" />
        <span className="font-medium text-foreground">{formatAuditLabel(entry.action)}</span>
        <span aria-hidden="true">·</span>
        <span>{formatOccurredAt(entry.occurredAt)}</span>
      </div>
    </li>
  )
}

export default ConversationTimeline
